import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  feedingTypesAPI,
  nutritionProfileAPI,
  healthProfileAPI,
  mealGroupsAPI,
  feedGroupsAPI,
  nutritionTargetsAPI,
  nutritionSymptomsAPI,
  nutritionEntryMoveAPI,
  mealPlanAPI,
  foodSearchAPI,
  foodTypesAPI,
  feedSearchAPI,
  foodPreferenceAPI,
} from "@/lib/nutrition-evaluation-api";
import type { MoveEntryPayload } from "@/lib/nutrition-evaluation-api";
import type {
  NutritionProfile,
  MealData,
  FeedData,
  HealthProfileUpdateSource,
  NutritionTarget,
  SleepData,
  FoodFrequency,
  DietaryPreference,
  MealEntry,
  FeedEntry,
  FoodPreferenceItem,
} from "@/types/nutrition";

// =============================================================================
// Query Keys
// =============================================================================

export const nutritionQueryKeys = {
  feedingTypes: ["feeding-types"] as const,
  appointmentFeedingType: (appointmentId: string) =>
    ["appointment-feeding-type", appointmentId] as const,
  nutritionProfile: (appointmentId: string) =>
    ["nutrition-profile", appointmentId] as const,
  healthProfile: (appointmentId: string) =>
    ["health-profile", appointmentId] as const,
  sleepData: (appointmentId: string) => ["sleep-data", appointmentId] as const,
  foodFrequency: (appointmentId: string) =>
    ["food-frequency", appointmentId] as const,
  dietaryPreference: (appointmentId: string) =>
    ["dietary-preference", appointmentId] as const,
  dietRecall: (appointmentId: string, isTubeFeeding: boolean) =>
    ["diet-recall", appointmentId, isTubeFeeding] as const,
  mealPlan: (
    appointmentId: string,
    planIndex: number,
    isTubeFeeding: boolean
  ) => ["meal-plan", appointmentId, planIndex, isTubeFeeding] as const,
  mealPlans: (appointmentId: string) => ["meal-plans", appointmentId] as const,
  mealStrategy: (appointmentId: string) =>
    ["meal-strategy", appointmentId] as const,
  nutritionTarget: (appointmentId: string) =>
    ["nutrition-target", appointmentId] as const,
  weightEnergyTarget: (appointmentId: string) =>
    ["weight-energy-target", appointmentId] as const,
  macronutrientTarget: (appointmentId: string) =>
    ["macronutrient-target", appointmentId] as const,
  specialFeedOptions: ["special-feed-options"] as const,
  specialFeedConsiderations: (appointmentId: string) =>
    ["special-feed-considerations", appointmentId] as const,
  nutritionSymptoms: ["nutrition-symptoms"] as const,
  appointmentSymptoms: (appointmentId: string) =>
    ["appointment-symptoms", appointmentId] as const,
  symptomSearch: (query: string, appointmentId: string, type: string) =>
    ["symptom-search", query, appointmentId, type] as const,
  foodSearch: (
    query: string,
    appointmentId?: string,
    foodType?: string,
    source?: string
  ) => ["food-search", query, appointmentId, foodType, source] as const,
  foodTypes: ["food-types"] as const,
  feedSearch: (query: string) => ["feed-search", query] as const,
  foodServings: (foodItemId: number) => ["food-servings", foodItemId] as const,
  likedFood: (appointmentId: string) => ["liked-food", appointmentId] as const,
  dislikedFood: (appointmentId: string) =>
    ["disliked-food", appointmentId] as const,
  foodPreferenceSearch: (
    query: string,
    appointmentId?: string,
    isLiked?: boolean
  ) => ["food-preference-search", query, appointmentId, isLiked] as const,
};

// =============================================================================
// Feeding Types Hooks
// =============================================================================

export const useFeedingTypes = () => {
  return useQuery({
    queryKey: nutritionQueryKeys.feedingTypes,
    queryFn: feedingTypesAPI.list,
    staleTime: 1000 * 60 * 30, // 30 minutes
  });
};

export const useAppointmentFeedingType = (appointmentId: string) => {
  return useQuery({
    queryKey: nutritionQueryKeys.appointmentFeedingType(appointmentId),
    queryFn: () => feedingTypesAPI.getAppointmentFeedingType(appointmentId),
    enabled: !!appointmentId,
  });
};

export const useSetFeedingType = (appointmentId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      feedingTypeId,
      existingRecordId,
    }: {
      feedingTypeId: number;
      existingRecordId?: string;
    }) => {
      if (existingRecordId) {
        // PATCH - update existing appointment feeding type
        return feedingTypesAPI.updateAppointmentFeedingType(
          appointmentId,
          existingRecordId,
          feedingTypeId
        );
      }
      // POST - create new appointment feeding type
      return feedingTypesAPI.setAppointmentFeedingType(
        appointmentId,
        feedingTypeId
      );
    },
    onSuccess: (_data) => {
      // Invalidate appointment feeding type
      queryClient.invalidateQueries({
        queryKey: nutritionQueryKeys.appointmentFeedingType(appointmentId),
      });
      // Invalidate diet recall data as feeding type change affects it
      queryClient.invalidateQueries({
        queryKey: ["diet-recall", appointmentId],
      });
      // Invalidate all meal plan data
      queryClient.invalidateQueries({
        queryKey: ["meal-plan", appointmentId],
      });
      // Invalidate special feed considerations (only relevant for tube feeding)
      queryClient.invalidateQueries({
        queryKey: nutritionQueryKeys.specialFeedConsiderations(appointmentId),
      });
      // Also invalidate meal plans list
      queryClient.invalidateQueries({
        queryKey: nutritionQueryKeys.mealPlans(appointmentId),
      });
    },
  });
};

// =============================================================================
// Nutrition Profile Hooks
// =============================================================================

export const useNutritionProfile = (appointmentId: string) => {
  return useQuery({
    queryKey: nutritionQueryKeys.nutritionProfile(appointmentId),
    queryFn: () => nutritionProfileAPI.get(appointmentId),
    enabled: !!appointmentId,
  });
};

// Health Profile Hook (for body measurements, BMI, TDEE, BMR, PA Calories, etc.)
// This is the main source of truth for body measurements used across all nutrition tabs
export const useHealthProfile = (appointmentId: string) => {
  return useQuery({
    queryKey: nutritionQueryKeys.healthProfile(appointmentId),
    queryFn: () => healthProfileAPI.get(appointmentId),
    enabled: !!appointmentId,
  });
};

// Update Health Profile - updates body measurements and recalculates BMI, TDEE, BMR, PA Calories
export const useUpdateHealthProfile = (appointmentId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      weight_in_kgs?: number;
      height_in_cms?: number;
      waist_in_cms?: number;
      hip_in_cms?: number;
      activity_level?: string;
      source?: HealthProfileUpdateSource;
    }) => {
      return healthProfileAPI.update(appointmentId, data);
    },
    onSuccess: () => {
      // Invalidate health profile to refetch calculated values
      queryClient.invalidateQueries({
        queryKey: nutritionQueryKeys.healthProfile(appointmentId),
      });
      // Also invalidate nutrition profile as it may be affected
      queryClient.invalidateQueries({
        queryKey: nutritionQueryKeys.nutritionProfile(appointmentId),
      });
    },
  });
};

export const useUpdateNutritionProfile = (appointmentId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      profileId,
      data,
    }: {
      profileId?: string;
      data: Partial<NutritionProfile>;
    }) => {
      if (profileId) {
        return nutritionProfileAPI.update(profileId, data);
      }
      return nutritionProfileAPI.create({
        appointment: appointmentId,
        ...data,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: nutritionQueryKeys.nutritionProfile(appointmentId),
      });
    },
  });
};

export const useSleepData = (appointmentId: string) => {
  return useQuery({
    queryKey: nutritionQueryKeys.sleepData(appointmentId),
    queryFn: () => nutritionProfileAPI.getSleepData(appointmentId),
    enabled: !!appointmentId,
  });
};

export const useUpdateSleepData = (appointmentId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      data,
      isNew = true,
    }: {
      data: SleepData;
      isNew?: boolean;
    }) => nutritionProfileAPI.updateSleepData(appointmentId, data, isNew),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: nutritionQueryKeys.sleepData(appointmentId),
      });
    },
  });
};

export const useFoodFrequency = (appointmentId: string) => {
  return useQuery({
    queryKey: nutritionQueryKeys.foodFrequency(appointmentId),
    queryFn: () => nutritionProfileAPI.getFoodFrequency(appointmentId),
    enabled: !!appointmentId,
  });
};

export const useUpdateFoodFrequency = (appointmentId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      data,
      isNew = true,
    }: {
      data: FoodFrequency;
      isNew?: boolean;
    }) => nutritionProfileAPI.updateFoodFrequency(appointmentId, data, isNew),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: nutritionQueryKeys.foodFrequency(appointmentId),
      });
    },
  });
};

export const useDietaryPreference = (appointmentId: string) => {
  return useQuery({
    queryKey: nutritionQueryKeys.dietaryPreference(appointmentId),
    queryFn: () => nutritionProfileAPI.getDietaryPreference(appointmentId),
    enabled: !!appointmentId,
  });
};

export const useUpdateDietaryPreference = (appointmentId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      data,
      isNew = true,
    }: {
      data: DietaryPreference;
      isNew?: boolean;
    }) =>
      nutritionProfileAPI.updateDietaryPreference(appointmentId, data, isNew),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: nutritionQueryKeys.dietaryPreference(appointmentId),
      });
    },
  });
};

// =============================================================================
// Diet Recall & Meal Data Hooks
// =============================================================================

export const useDietRecall = (
  appointmentId: string,
  isTubeFeeding: boolean = false
) => {
  return useQuery<MealData | FeedData | null>({
    queryKey: nutritionQueryKeys.dietRecall(appointmentId, isTubeFeeding),
    queryFn: async () => {
      if (isTubeFeeding) {
        return feedGroupsAPI.getDietRecall(appointmentId);
      }
      return mealGroupsAPI.getDietRecall(appointmentId);
    },
    enabled: !!appointmentId,
  });
};

export const useMealPlanData = (
  appointmentId: string,
  planIndex: number,
  isTubeFeeding: boolean = false,
  enabled: boolean = true
) => {
  return useQuery<MealData | FeedData | null>({
    queryKey: nutritionQueryKeys.mealPlan(
      appointmentId,
      planIndex,
      isTubeFeeding
    ),
    queryFn: async () => {
      if (isTubeFeeding) {
        return feedGroupsAPI.getMealPlan(appointmentId, planIndex);
      }
      return mealGroupsAPI.getMealPlan(appointmentId, planIndex);
    },
    enabled: !!appointmentId && enabled,
  });
};

/**
 * Hook to add a meal entry (food item).
 * Matches Flutter's addFoodToMealGroup which uses:
 * - isMealPlan ? Apis().mealPlanEntry(appointmentId) : Apis().dietRecallEntry(appointmentId)
 * For tube feeding, can add either:
 * 1. Feed items with volume_ml (formula feeds)
 * 2. Food items with serving_size + quantity (regular food added to feed groups)
 */
export const useAddMealEntry = (
  appointmentId: string,
  isTubeFeeding: boolean = false,
  isMealPlan: boolean = false
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      data: // Regular meal entry (oral feeding)
        | {
            meal_group: string;
            food_item: number;
            serving_size: number;
            quantity: string;
          }
        // Feed item entry (tube feeding formula)
        | { feed_group: string; feed_item: number; volume_ml: string }
        // Food item added to feed group (tube feeding with regular food)
        | {
            feed_group: string;
            food_item: number;
            serving_size: number;
            quantity: string;
          }
    ) => {
      if (isTubeFeeding) {
        // For tube feeding, use feed entry endpoints
        const endpoint = isMealPlan
          ? `/nutrition/${appointmentId}/feed-entry/`
          : `/nutrition/${appointmentId}/diet-recall/feed-entry/`;

        // Import api from lib
        const { default: api } = await import("@/lib/api");
        const response = await api.post(endpoint, data);
        return response.data;
      }
      // Use the correct endpoint based on isMealPlan for oral feeding
      const mealData = data as {
        meal_group: string;
        food_item: number;
        serving_size: number;
        quantity: string;
      };
      return mealGroupsAPI.addEntry(appointmentId, isMealPlan, mealData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["diet-recall", appointmentId],
      });
      queryClient.invalidateQueries({
        queryKey: ["meal-plan", appointmentId],
      });
    },
  });
};

/**
 * Hook to add a note to a meal group.
 * Matches Flutter's addNoteToMealGroup which uses:
 * - isMealPlan ? Apis().mealPlanEntry(appointmentId) : Apis().dietRecallEntry(appointmentId)
 */
export const useAddMealNote = (
  appointmentId: string,
  isTubeFeeding: boolean = false,
  isMealPlan: boolean = false
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: { group_id: string; comment: string }) => {
      if (isTubeFeeding) {
        return feedGroupsAPI.addNote(appointmentId, isMealPlan, {
          feed_group: data.group_id,
          comment: data.comment,
          is_comment: true,
        });
      }
      return mealGroupsAPI.addNote(appointmentId, isMealPlan, {
        meal_group: data.group_id,
        comment: data.comment,
        is_comment: true,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["diet-recall", appointmentId],
      });
      queryClient.invalidateQueries({
        queryKey: ["meal-plan", appointmentId],
      });
    },
  });
};

/**
 * Hook to update a meal entry.
 * Matches Flutter's updateFoodEntry which uses:
 * - isMealPlan ? Apis().mealPlanEntry(appointmentId) : Apis().dietRecallEntry(appointmentId)
 */
export const useUpdateMealEntry = (
  appointmentId: string,
  isTubeFeeding: boolean = false,
  isMealPlan: boolean = false
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      entryId,
      data,
    }: {
      entryId: string;
      data: Partial<MealEntry | FeedEntry>;
    }) => {
      if (isTubeFeeding) {
        return feedGroupsAPI.updateEntry(
          appointmentId,
          isMealPlan,
          entryId,
          data as Partial<FeedEntry>
        );
      }
      return mealGroupsAPI.updateEntry(
        appointmentId,
        isMealPlan,
        entryId,
        data as Partial<MealEntry>
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["diet-recall", appointmentId],
      });
      queryClient.invalidateQueries({
        queryKey: ["meal-plan", appointmentId],
      });
    },
  });
};

/**
 * Hook to delete a meal entry.
 * Matches Flutter's deleteFoodEntry which uses:
 * - isMealPlan ? Apis().mealPlanEntry(appointmentId) : Apis().dietRecallEntry(appointmentId)
 */
export const useDeleteMealEntry = (
  appointmentId: string,
  isTubeFeeding: boolean = false,
  isMealPlan: boolean = false
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (entryId: string) => {
      if (isTubeFeeding) {
        return feedGroupsAPI.deleteEntry(appointmentId, isMealPlan, entryId);
      }
      return mealGroupsAPI.deleteEntry(appointmentId, isMealPlan, entryId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["diet-recall", appointmentId],
      });
      queryClient.invalidateQueries({
        queryKey: ["meal-plan", appointmentId],
      });
    },
  });
};

export const useMoveNutritionEntry = (appointmentId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: MoveEntryPayload) => {
      return nutritionEntryMoveAPI.moveEntry(appointmentId, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["diet-recall", appointmentId],
      });
      queryClient.invalidateQueries({
        queryKey: ["meal-plan", appointmentId],
      });
    },
  });
};

// =============================================================================
// Feed Group CRUD Hooks (for Tube Feeding)
// =============================================================================

/**
 * Hook to create a feed group (for tube feeding).
 * Matches Flutter's createFeedGroup which uses different endpoints for meal plan vs diet recall.
 */
export const useCreateFeedGroup = (
  appointmentId: string,
  isMealPlan: boolean = false,
  planIndex: number = 0
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: { time: string }) => {
      // For meal plan, include the option and plan_type
      const requestData = isMealPlan
        ? {
            ...data,
            option: `OPT${planIndex + 1}`,
            plan_type: "meal_plan",
          }
        : data;
      return feedGroupsAPI.createFeedGroup(
        appointmentId,
        isMealPlan,
        requestData
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["diet-recall", appointmentId],
      });
      queryClient.invalidateQueries({
        queryKey: ["meal-plan", appointmentId],
      });
    },
  });
};

/**
 * Hook to update a feed group (for tube feeding).
 */
export const useUpdateFeedGroup = (
  appointmentId: string,
  isMealPlan: boolean = false,
  _planIndex: number = 0
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      feedGroupId,
      data,
    }: {
      feedGroupId: string;
      data: { time?: string };
    }) => {
      return feedGroupsAPI.updateFeedGroup(
        appointmentId,
        isMealPlan,
        feedGroupId,
        data
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["diet-recall", appointmentId],
      });
      queryClient.invalidateQueries({
        queryKey: ["meal-plan", appointmentId],
      });
    },
  });
};

/**
 * Hook to delete a feed group (for tube feeding).
 */
export const useDeleteFeedGroup = (
  appointmentId: string,
  isMealPlan: boolean = false,
  _planIndex: number = 0
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (feedGroupId: string) => {
      return feedGroupsAPI.deleteFeedGroup(
        appointmentId,
        isMealPlan,
        feedGroupId
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["diet-recall", appointmentId],
      });
      queryClient.invalidateQueries({
        queryKey: ["meal-plan", appointmentId],
      });
    },
  });
};

// =============================================================================
// Meal Plans Hooks
// =============================================================================

export const useMealPlans = (appointmentId: string) => {
  return useQuery({
    queryKey: nutritionQueryKeys.mealPlans(appointmentId),
    queryFn: () => mealPlanAPI.list(appointmentId),
    enabled: !!appointmentId,
  });
};

export const useCreateMealPlan = (appointmentId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (name: string) => mealPlanAPI.create(appointmentId, name),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: nutritionQueryKeys.mealPlans(appointmentId),
      });
    },
  });
};

/**
 * Hook to copy diet recall entries to meal plan.
 * Implements client-side copying since backend endpoint doesn't exist.
 * Matches Flutter behavior of copying entries by matching meal_time codes.
 */
export const useCopyDietRecallToMealPlan = (appointmentId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (planIndex: number) => {
      // 1. Fetch diet recall data
      const dietRecallData = await mealGroupsAPI.getDietRecall(appointmentId);
      if (!dietRecallData || !dietRecallData.meal_groups) {
        throw new Error("No diet recall data found");
      }

      // 2. Fetch meal plan data to get meal group IDs
      const mealPlanData = await mealGroupsAPI.getMealPlan(
        appointmentId,
        planIndex
      );
      if (!mealPlanData || !mealPlanData.meal_groups) {
        throw new Error("No meal plan data found");
      }

      // 3. Create a map of meal_time to meal plan group ID
      const mealPlanGroupMap = new Map<string, string>();
      for (const group of mealPlanData.meal_groups) {
        mealPlanGroupMap.set(group.meal_time, group.id);
      }

      // 4. Copy each entry from diet recall to meal plan
      const copyPromises: Promise<unknown>[] = [];
      for (const dietRecallGroup of dietRecallData.meal_groups) {
        const mealPlanGroupId = mealPlanGroupMap.get(dietRecallGroup.meal_time);
        if (!mealPlanGroupId) continue; // Skip if no matching meal plan group

        for (const entry of dietRecallGroup.entries) {
          if (entry.is_comment) {
            // Copy comment
            copyPromises.push(
              mealGroupsAPI.addNote(appointmentId, true, {
                meal_group: mealPlanGroupId,
                comment: entry.comment,
                is_comment: true,
                order: entry.order,
              })
            );
          } else if (entry.food_item && entry.serving_size) {
            // Copy food item
            copyPromises.push(
              mealGroupsAPI.addEntry(appointmentId, true, {
                meal_group: mealPlanGroupId,
                food_item: entry.food_item,
                serving_size: entry.serving_size,
                quantity: entry.quantity,
                order: entry.order,
              })
            );
          }
        }
      }

      // Execute all copy operations in parallel
      await Promise.all(copyPromises);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["meal-plan", appointmentId],
      });
      queryClient.invalidateQueries({
        queryKey: ["diet-recall", appointmentId],
      });
    },
  });
};

/**
 * Copy entries from one meal-plan option to another (e.g. Option 1 → Option 2).
 * Mirrors useCopyDietRecallToMealPlan but reads from a meal-plan source.
 * Copies are additive — existing entries in the target option are preserved.
 */
export const useCopyMealPlanOption = (appointmentId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      sourcePlanIndex,
      targetPlanIndex,
    }: {
      sourcePlanIndex: number;
      targetPlanIndex: number;
    }) => {
      const sourceData = await mealGroupsAPI.getMealPlan(
        appointmentId,
        sourcePlanIndex
      );
      if (!sourceData || !sourceData.meal_groups) {
        throw new Error("No source meal plan data found");
      }

      const targetData = await mealGroupsAPI.getMealPlan(
        appointmentId,
        targetPlanIndex
      );
      if (!targetData || !targetData.meal_groups) {
        throw new Error("No target meal plan data found");
      }

      const targetGroupMap = new Map<string, string>();
      for (const group of targetData.meal_groups) {
        targetGroupMap.set(group.meal_time, group.id);
      }

      const copyPromises: Promise<unknown>[] = [];
      for (const sourceGroup of sourceData.meal_groups) {
        const targetGroupId = targetGroupMap.get(sourceGroup.meal_time);
        if (!targetGroupId) continue;

        for (const entry of sourceGroup.entries) {
          if (entry.is_comment) {
            copyPromises.push(
              mealGroupsAPI.addNote(appointmentId, true, {
                meal_group: targetGroupId,
                comment: entry.comment,
                is_comment: true,
                order: entry.order,
              })
            );
          } else if (entry.food_item && entry.serving_size) {
            copyPromises.push(
              mealGroupsAPI.addEntry(appointmentId, true, {
                meal_group: targetGroupId,
                food_item: entry.food_item,
                serving_size: entry.serving_size,
                quantity: entry.quantity,
                order: entry.order,
              })
            );
          }
        }
      }

      await Promise.all(copyPromises);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["meal-plan", appointmentId],
      });
    },
  });
};

// =============================================================================
// Targets Hooks
// =============================================================================

export const useMealStrategy = (appointmentId: string) => {
  return useQuery({
    queryKey: nutritionQueryKeys.mealStrategy(appointmentId),
    queryFn: () => nutritionTargetsAPI.getMealStrategy(appointmentId),
    enabled: !!appointmentId,
  });
};

export const useUpdateMealStrategy = (appointmentId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      strategies,
      existingId,
    }: {
      strategies: string[];
      existingId?: string;
    }) =>
      nutritionTargetsAPI.updateMealStrategy(
        appointmentId,
        strategies,
        existingId
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: nutritionQueryKeys.mealStrategy(appointmentId),
      });
    },
  });
};

// Nutrition Target - Combined weight/energy and macronutrient targets
export const useNutritionTarget = (appointmentId: string) => {
  return useQuery({
    queryKey: nutritionQueryKeys.nutritionTarget(appointmentId),
    queryFn: () => nutritionTargetsAPI.getNutritionTarget(appointmentId),
    enabled: !!appointmentId,
  });
};

export const useUpdateNutritionTarget = (appointmentId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      data,
      isNew = false,
    }: {
      data: Partial<NutritionTarget>;
      isNew?: boolean;
    }) => nutritionTargetsAPI.updateNutritionTarget(appointmentId, data, isNew),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: nutritionQueryKeys.nutritionTarget(appointmentId),
      });
      // Also invalidate legacy query keys for backwards compatibility
      queryClient.invalidateQueries({
        queryKey: nutritionQueryKeys.weightEnergyTarget(appointmentId),
      });
      queryClient.invalidateQueries({
        queryKey: nutritionQueryKeys.macronutrientTarget(appointmentId),
      });
    },
  });
};

export const useWeightEnergyTarget = (appointmentId: string) => {
  return useQuery({
    queryKey: nutritionQueryKeys.weightEnergyTarget(appointmentId),
    queryFn: () => nutritionTargetsAPI.getNutritionTarget(appointmentId),
    enabled: !!appointmentId,
  });
};

export const useUpdateWeightEnergyTarget = (appointmentId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      data,
      isNew = false,
    }: {
      data: Partial<NutritionTarget>;
      isNew?: boolean;
    }) => nutritionTargetsAPI.updateNutritionTarget(appointmentId, data, isNew),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: nutritionQueryKeys.weightEnergyTarget(appointmentId),
      });
      queryClient.invalidateQueries({
        queryKey: nutritionQueryKeys.nutritionTarget(appointmentId),
      });
    },
  });
};

export const useMacronutrientTarget = (appointmentId: string) => {
  return useQuery({
    queryKey: nutritionQueryKeys.macronutrientTarget(appointmentId),
    queryFn: () => nutritionTargetsAPI.getNutritionTarget(appointmentId),
    enabled: !!appointmentId,
  });
};

export const useUpdateMacronutrientTarget = (appointmentId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      data,
      isNew = false,
    }: {
      data: Partial<NutritionTarget>;
      isNew?: boolean;
    }) => nutritionTargetsAPI.updateNutritionTarget(appointmentId, data, isNew),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: nutritionQueryKeys.macronutrientTarget(appointmentId),
      });
      queryClient.invalidateQueries({
        queryKey: nutritionQueryKeys.nutritionTarget(appointmentId),
      });
    },
  });
};

export const useSpecialFeedConsiderations = (appointmentId: string) => {
  return useQuery({
    queryKey: nutritionQueryKeys.specialFeedConsiderations(appointmentId),
    queryFn: () =>
      nutritionTargetsAPI.getSpecialFeedConsiderations(appointmentId),
    enabled: !!appointmentId,
  });
};

export const useSearchSpecialFeedOptions = () => {
  return useMutation({
    mutationFn: (query: string) =>
      nutritionTargetsAPI.searchSpecialFeedOptions(query),
  });
};

export const useUpdateSpecialFeedConsiderations = (appointmentId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (considerationIds: number[]) =>
      nutritionTargetsAPI.updateSpecialFeedConsiderations(
        appointmentId,
        considerationIds
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: nutritionQueryKeys.specialFeedConsiderations(appointmentId),
      });
    },
  });
};

export const useMealStrategyHistory = (appointmentId: string) => {
  return useQuery({
    queryKey: [...nutritionQueryKeys.mealStrategy(appointmentId), "history"],
    queryFn: () => nutritionTargetsAPI.getMealStrategyHistory(appointmentId),
    enabled: !!appointmentId,
  });
};

// =============================================================================
// Nutrition Symptoms Hooks
// =============================================================================

export const useNutritionSymptoms = () => {
  return useQuery({
    queryKey: nutritionQueryKeys.nutritionSymptoms,
    queryFn: nutritionSymptomsAPI.list,
    staleTime: 1000 * 60 * 30,
  });
};

// Search symptoms with query, appointmentId, and type (matching Flutter)
// When query is empty, fetches all symptoms for the type
export const useSearchNutritionSymptoms = (
  query: string,
  appointmentId: string,
  type: "digestive" | "others",
  enabled: boolean = true
) => {
  return useQuery({
    queryKey: nutritionQueryKeys.symptomSearch(query, appointmentId, type),
    queryFn: () => nutritionSymptomsAPI.search(query, appointmentId, type),
    enabled: enabled && !!appointmentId, // Always fetch when enabled - empty query returns all
    staleTime: 1000 * 60 * 5,
  });
};

export const useAppointmentNutritionSymptoms = (appointmentId: string) => {
  return useQuery({
    queryKey: nutritionQueryKeys.appointmentSymptoms(appointmentId),
    queryFn: () => nutritionSymptomsAPI.getAppointmentSymptoms(appointmentId),
    enabled: !!appointmentId,
    staleTime: 0,
    refetchOnMount: "always",
  });
};

export const useAddNutritionSymptom = (appointmentId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ symptomId, notes }: { symptomId: number; notes?: string }) =>
      nutritionSymptomsAPI.addSymptom(appointmentId, symptomId, notes),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: nutritionQueryKeys.appointmentSymptoms(appointmentId),
      });
      // Symptoms tab reads the same endpoint under a different key — keep it in sync.
      queryClient.invalidateQueries({
        queryKey: ["patientSymptoms", appointmentId],
      });
    },
  });
};

export const useRemoveNutritionSymptom = (appointmentId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => nutritionSymptomsAPI.removeSymptom(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: nutritionQueryKeys.appointmentSymptoms(appointmentId),
      });
      queryClient.invalidateQueries({
        queryKey: ["patientSymptoms", appointmentId],
      });
    },
  });
};

// =============================================================================
// Food & Feed Search Hooks
// =============================================================================

export const useFoodSearch = (
  query: string,
  appointmentId?: string,
  enabled: boolean = true,
  foodType?: string,
  source?: string
) => {
  return useQuery({
    queryKey: nutritionQueryKeys.foodSearch(
      query,
      appointmentId,
      foodType,
      source
    ),
    queryFn: () => foodSearchAPI.search(query, appointmentId, foodType, source),
    enabled: enabled && query.length >= 2,
    staleTime: 1000 * 60 * 5,
  });
};

export const useFoodTypes = () => {
  return useQuery({
    queryKey: nutritionQueryKeys.foodTypes,
    queryFn: () => foodTypesAPI.list(),
    staleTime: Infinity,
  });
};

export const useFoodServings = (
  foodItemId: number,
  enabled: boolean = true
) => {
  return useQuery({
    queryKey: nutritionQueryKeys.foodServings(foodItemId),
    queryFn: () => foodSearchAPI.getServings(foodItemId),
    enabled: enabled && foodItemId > 0,
  });
};

export const useFeedSearch = (query: string, enabled: boolean = true) => {
  return useQuery({
    queryKey: nutritionQueryKeys.feedSearch(query),
    queryFn: () => feedSearchAPI.search(query),
    enabled: enabled && query.length >= 2,
    staleTime: 1000 * 60 * 5,
  });
};

// =============================================================================
// Food Preference Hooks (Liked/Disliked Food)
// =============================================================================

export const useLikedFood = (appointmentId: string) => {
  return useQuery<FoodPreferenceItem[]>({
    queryKey: nutritionQueryKeys.likedFood(appointmentId),
    queryFn: () => foodPreferenceAPI.getPreferences(appointmentId, true),
    enabled: !!appointmentId,
  });
};

export const useDislikedFood = (appointmentId: string) => {
  return useQuery<FoodPreferenceItem[]>({
    queryKey: nutritionQueryKeys.dislikedFood(appointmentId),
    queryFn: () => foodPreferenceAPI.getPreferences(appointmentId, false),
    enabled: !!appointmentId,
  });
};

export const useAddFoodPreference = (appointmentId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      foodItemId,
      isLiked,
    }: {
      foodItemId: number;
      isLiked: boolean;
    }) => foodPreferenceAPI.addPreference(appointmentId, foodItemId, isLiked),
    onSuccess: (_, { isLiked }) => {
      // Invalidate the appropriate food preference list
      queryClient.invalidateQueries({
        queryKey: isLiked
          ? nutritionQueryKeys.likedFood(appointmentId)
          : nutritionQueryKeys.dislikedFood(appointmentId),
      });
    },
  });
};

export const useRemoveFoodPreference = (appointmentId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      preferenceId,
      isLiked,
    }: {
      preferenceId: string;
      isLiked: boolean;
    }) => foodPreferenceAPI.removePreference(appointmentId, preferenceId),
    onSuccess: (_, { isLiked }) => {
      // Invalidate the appropriate food preference list
      queryClient.invalidateQueries({
        queryKey: isLiked
          ? nutritionQueryKeys.likedFood(appointmentId)
          : nutritionQueryKeys.dislikedFood(appointmentId),
      });
    },
  });
};

export const useFoodPreferenceSearch = (
  query: string,
  appointmentId?: string,
  isLiked?: boolean,
  enabled: boolean = true
) => {
  return useQuery({
    queryKey: nutritionQueryKeys.foodPreferenceSearch(
      query,
      appointmentId,
      isLiked
    ),
    queryFn: () =>
      foodPreferenceAPI.searchFoodItems(query, appointmentId, isLiked),
    enabled: enabled && query.length >= 2,
    staleTime: 1000 * 60 * 5,
  });
};
